import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Briefcase, FileText, Bell, TrendingUp, Calendar, Users, 
  Clock, CheckCircle2, AlertCircle, Scale, Gavel, FileCheck,
  Activity, DollarSign, UserCheck, BarChart3, PieChart, 
  MessageSquare, Award, Target, Zap, Bot, Sparkles
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';

const EnhancedDashboardPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [stats, setStats] = useState(null);
  const [cases, setCases] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, casesRes, notificationsRes] = await Promise.all([
        api.get('/stats/dashboard'),
        api.get('/cases'),
        api.get('/notifications'),
      ]);
      
      setStats(statsRes.data);
      setCases(casesRes.data.slice(0, 5)); // Last 5 cases
      setNotifications(notificationsRes.data.slice(0, 5)); // Last 5 notifications
    } catch (error) {
      toast.error(
        i18n.language === 'ar' ? 'فشل تحميل البيانات' : 'Failed to load data'
      );
    } finally {
      setLoading(false);
    }
  };

  // Enhanced stats for clients
  const clientStats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي القضايا' : 'Total Cases',
      value: stats?.total_cases || 0,
      icon: Briefcase,
      gradient: 'from-blue-500 to-blue-600',
      change: '+12%',
      testId: 'total-cases-stat'
    },
    {
      title: i18n.language === 'ar' ? 'القضايا النشطة' : 'Active Cases',
      value: stats?.active_cases || 0,
      icon: TrendingUp,
      gradient: 'from-green-500 to-green-600',
      change: '+8%',
      testId: 'active-cases-stat'
    },
    {
      title: i18n.language === 'ar' ? 'المستندات' : 'Documents',
      value: stats?.total_documents || 0,
      icon: FileText,
      gradient: 'from-purple-500 to-purple-600',
      change: '+15%',
      testId: 'documents-stat'
    },
    {
      title: i18n.language === 'ar' ? 'الإشعارات' : 'Notifications',
      value: stats?.unread_notifications || 0,
      icon: Bell,
      gradient: 'from-orange-500 to-orange-600',
      change: 'جديد',
      testId: 'notifications-stat'
    },
    {
      title: i18n.language === 'ar' ? 'الجلسات القادمة' : 'Upcoming Hearings',
      value: stats?.upcoming_hearings || 0,
      icon: Calendar,
      gradient: 'from-red-500 to-red-600',
      change: 'قريباً',
      testId: 'hearings-stat'
    },
    {
      title: i18n.language === 'ar' ? 'معدل النجاح' : 'Success Rate',
      value: '94%',
      icon: Award,
      gradient: 'from-yellow-500 to-yellow-600',
      change: '+2%',
      testId: 'success-rate-stat'
    },
  ];

  // Activity data
  const recentActivity = [
    {
      icon: FileCheck,
      title: i18n.language === 'ar' ? 'تم رفع مستند جديد' : 'New Document Uploaded',
      time: i18n.language === 'ar' ? 'منذ ساعتين' : '2 hours ago',
      type: 'document'
    },
    {
      icon: Gavel,
      title: i18n.language === 'ar' ? 'جلسة قادمة غداً' : 'Hearing Tomorrow',
      time: i18n.language === 'ar' ? 'غداً 10:00 ص' : 'Tomorrow 10:00 AM',
      type: 'hearing'
    },
    {
      icon: CheckCircle2,
      title: i18n.language === 'ar' ? 'تم إغلاق قضية بنجاح' : 'Case Closed Successfully',
      time: i18n.language === 'ar' ? 'منذ 3 أيام' : '3 days ago',
      type: 'success'
    },
    {
      icon: MessageSquare,
      title: i18n.language === 'ar' ? 'رد جديد على تذكرة' : 'New Ticket Reply',
      time: i18n.language === 'ar' ? 'منذ 5 ساعات' : '5 hours ago',
      type: 'message'
    },
  ];

  // Case progress data
  const caseProgress = {
    total: stats?.total_cases || 0,
    active: stats?.active_cases || 0,
    pending: Math.floor((stats?.total_cases || 0) * 0.2),
    closed: Math.floor((stats?.total_cases || 0) * 0.3),
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100" data-testid="enhanced-dashboard">
      <Navbar />

      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          {/* Welcome Header with Gradient */}
          <div className="relative overflow-hidden rounded-3xl p-8 md:p-12" 
               style={{ background: 'var(--gradient-hero)' }}>
            <div className="relative z-10 fade-in">
              <div className="flex flex-col items-center lg:items-start gap-6 mb-6">
                {/* شعار وازن الكبير */}
                <div className="mb-4">
                  <img 
                    src="/wazin-logo.jpg" 
                    alt="Wazin Logo" 
                    className="h-28 md:h-36 w-auto object-contain brightness-0 invert drop-shadow-2xl"
                  />
                </div>
              </div>
              
              <div className="mb-4">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  {i18n.language === 'ar' ? `مرحباً ${user?.full_name}` : `Welcome ${user?.full_name}`}
                </h2>
                <p className="text-white/80 text-lg">
                  {i18n.language === 'ar'
                    ? 'نظام إدارة القضايا المتكامل'
                    : 'Integrated Case Management System'
                  }
                </p>
              </div>
              <div className="flex flex-wrap gap-3 mt-6">
                <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-sm">
                  {user?.role === 'client' 
                    ? (i18n.language === 'ar' ? 'عميل' : 'Client')
                    : user?.role === 'lawyer'
                    ? (i18n.language === 'ar' ? 'محامي' : 'Lawyer')
                    : (i18n.language === 'ar' ? 'مدير' : 'Admin')
                  }
                </Badge>
                <Badge className="bg-white/20 text-white border-white/30 px-4 py-2 text-sm">
                  <CheckCircle2 className="w-4 h-4 ml-1" />
                  {i18n.language === 'ar' ? 'حساب نشط' : 'Active Account'}
                </Badge>
              </div>
            </div>
            
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-96 h-96 bg-primary/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-0 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />
          </div>

          {/* AI Assistant Quick Access */}
          <Card className="relative overflow-hidden border-0 shadow-xl bg-gradient-to-br from-purple-500 via-purple-600 to-indigo-600">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur-xl flex items-center justify-center border-2 border-white/30">
                    <Bot className="w-8 h-8 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-1">
                      {i18n.language === 'ar' ? 'المساعد القانوني الذكي' : 'AI Legal Assistant'}
                    </h3>
                    <p className="text-white/80 text-sm">
                      {i18n.language === 'ar' 
                        ? 'احصل على استشارات قانونية فورية باستخدام الذكاء الصناعي'
                        : 'Get instant legal consultations powered by AI'
                      }
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => navigate('/chat')}
                  className="bg-white text-purple-600 hover:bg-white/90 font-semibold shadow-lg"
                >
                  <Sparkles className="w-5 h-5 ml-2" />
                  {i18n.language === 'ar' ? 'ابدأ المحادثة' : 'Start Chat'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions - Admin Only */}
          {user?.role === 'admin' && (
            <div className="space-y-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {i18n.language === 'ar' ? 'الوصول السريع' : 'Quick Access'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Cases Management */}
                <Card 
                  onClick={() => navigate('/admin/cases-advanced')}
                  className="cursor-pointer hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg">
                        <Scale className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          {i18n.language === 'ar' ? 'إدارة القضايا' : 'Case Management'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {i18n.language === 'ar' ? 'إدارة شاملة للقضايا' : 'Comprehensive case management'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* HR - Employees */}
                <Card 
                  onClick={() => navigate('/hr/employees')}
                  className="cursor-pointer hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-green-50 to-green-100"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center shadow-lg">
                        <Users className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          {i18n.language === 'ar' ? 'إدارة الموظفين' : 'Employee Management'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {i18n.language === 'ar' ? 'ملفات الموظفين الكاملة' : 'Complete employee files'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* HR - Requests */}
                <Card 
                  onClick={() => navigate('/hr/requests')}
                  className="cursor-pointer hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-lg">
                        <FileCheck className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          {i18n.language === 'ar' ? 'الطلبات' : 'Requests'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {i18n.language === 'ar' ? 'إدارة طلبات الموظفين' : 'Manage employee requests'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tickets */}
                <Card 
                  onClick={() => navigate('/tickets')}
                  className="cursor-pointer hover:shadow-xl transition-all duration-300 border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100"
                >
                  <CardContent className="pt-6">
                    <div className="flex flex-col items-center text-center space-y-3">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg">
                        <MessageSquare className="w-8 h-8 text-white" />
                      </div>
                      <div>
                        <h4 className="font-bold text-lg text-gray-900">
                          {i18n.language === 'ar' ? 'التذاكر' : 'Tickets'}
                        </h4>
                        <p className="text-sm text-gray-600 mt-1">
                          {i18n.language === 'ar' ? 'نظام التذاكر الكامل' : 'Complete ticketing system'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Enhanced Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {clientStats.map((stat, index) => (
              <Card
                key={index}
                className="relative overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300 fade-in"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={stat.testId}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.gradient} opacity-5`} />
                <CardContent className="pt-6 relative z-10">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-text-secondary mb-2 font-medium">{stat.title}</p>
                      <div className="flex items-baseline gap-2">
                        <p className="text-4xl font-bold text-text-primary">{stat.value}</p>
                        {stat.change && (
                          <span className="text-sm font-semibold text-success">{stat.change}</span>
                        )}
                      </div>
                    </div>
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${stat.gradient} flex items-center justify-center shadow-lg`}>
                      <stat.icon className="w-8 h-8 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Main Content Tabs */}
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList className="grid w-full grid-cols-4 lg:w-auto">
              <TabsTrigger value="overview" className="gap-2">
                <Activity className="w-4 h-4" />
                {i18n.language === 'ar' ? 'نظرة عامة' : 'Overview'}
              </TabsTrigger>
              <TabsTrigger value="cases" className="gap-2">
                <Briefcase className="w-4 h-4" />
                {i18n.language === 'ar' ? 'القضايا' : 'Cases'}
              </TabsTrigger>
              <TabsTrigger value="activity" className="gap-2">
                <Zap className="w-4 h-4" />
                {i18n.language === 'ar' ? 'النشاط' : 'Activity'}
              </TabsTrigger>
              <TabsTrigger value="analytics" className="gap-2">
                <BarChart3 className="w-4 h-4" />
                {i18n.language === 'ar' ? 'الإحصائيات' : 'Analytics'}
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-primary" />
                      {i18n.language === 'ar' ? 'إجراءات سريعة' : 'Quick Actions'}
                    </CardTitle>
                    <CardDescription>
                      {i18n.language === 'ar'
                        ? 'الوصول السريع إلى المي��ات الرئيسية'
                        : 'Quick access to main features'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Button
                      onClick={() => navigate('/cases')}
                      className="w-full justify-start gap-3 h-14 text-lg"
                      style={{ background: 'var(--gradient-primary)' }}
                      data-testid="view-cases-btn"
                    >
                      <Briefcase className="w-5 h-5" />
                      {i18n.language === 'ar' ? 'عرض جميع القضايا' : 'View All Cases'}
                    </Button>
                    <Button
                      onClick={() => navigate('/documents')}
                      variant="outline"
                      className="w-full justify-start gap-3 h-14 text-lg border-2"
                      data-testid="view-documents-btn"
                    >
                      <FileText className="w-5 h-5" />
                      {i18n.language === 'ar' ? 'المستندات' : 'Documents'}
                    </Button>
                    <Button
                      onClick={() => navigate('/notifications')}
                      variant="outline"
                      className="w-full justify-start gap-3 h-14 text-lg border-2"
                      data-testid="view-notifications-btn"
                    >
                      <Bell className="w-5 h-5" />
                      <span className="flex-1 text-right">
                        {i18n.language === 'ar' ? 'الإشعارات' : 'Notifications'}
                      </span>
                      {stats?.unread_notifications > 0 && (
                        <Badge className="bg-error text-white">
                          {stats.unread_notifications}
                        </Badge>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Case Progress */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChart className="w-5 h-5 text-primary" />
                      {i18n.language === 'ar' ? 'تقدم القضايا' : 'Case Progress'}
                    </CardTitle>
                    <CardDescription>
                      {i18n.language === 'ar'
                        ? 'نظرة شاملة على حالة القضايا'
                        : 'Overview of case status'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {i18n.language === 'ar' ? 'القضايا النشطة' : 'Active Cases'}
                        </span>
                        <span className="font-semibold">{caseProgress.active}</span>
                      </div>
                      <Progress value={(caseProgress.active / caseProgress.total) * 100 || 0} className="h-2" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {i18n.language === 'ar' ? 'قيد الانتظار' : 'Pending Cases'}
                        </span>
                        <span className="font-semibold">{caseProgress.pending}</span>
                      </div>
                      <Progress value={(caseProgress.pending / caseProgress.total) * 100 || 0} className="h-2" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-text-secondary">
                          {i18n.language === 'ar' ? 'القضايا المغلقة' : 'Closed Cases'}
                        </span>
                        <span className="font-semibold">{caseProgress.closed}</span>
                      </div>
                      <Progress value={(caseProgress.closed / caseProgress.total) * 100 || 0} className="h-2" />
                    </div>

                    <div className="pt-4 border-t">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium">
                          {i18n.language === 'ar' ? 'الإجمالي' : 'Total'}
                        </span>
                        <span className="text-2xl font-bold text-primary">{caseProgress.total}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Cases Tab */}
            <TabsContent value="cases" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle>
                    {i18n.language === 'ar' ? 'أحدث القضايا' : 'Recent Cases'}
                  </CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar'
                      ? `عرض آخر ${cases.length} قضايا`
                      : `Showing last ${cases.length} cases`
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {cases.length === 0 ? (
                    <div className="text-center py-12">
                      <Briefcase className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                      <p className="text-text-secondary">
                        {i18n.language === 'ar' ? 'لا توجد قضايا بعد' : 'No cases yet'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {cases.map((caseItem, index) => (
                        <div
                          key={caseItem.id}
                          className="p-4 border rounded-lg hover:shadow-md transition-all cursor-pointer"
                          onClick={() => navigate(`/cases/${caseItem.id}`)}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant={caseItem.status === 'active' ? 'default' : 'secondary'}>
                                  {t(caseItem.status)}
                                </Badge>
                                <span className="text-sm text-text-secondary">{caseItem.case_number}</span>
                              </div>
                              <h4 className="font-semibold text-lg mb-1">{caseItem.title}</h4>
                              <p className="text-sm text-text-secondary line-clamp-1">
                                {caseItem.description}
                              </p>
                            </div>
                            <div className="text-right text-sm text-text-secondary">
                              {format(new Date(caseItem.created_at), 'dd MMM yyyy', {
                                locale: i18n.language === 'ar' ? ar : undefined
                              })}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Activity Tab */}
            <TabsContent value="activity" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Recent Activity */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Zap className="w-5 h-5 text-primary" />
                      {i18n.language === 'ar' ? 'النشاط الأخير' : 'Recent Activity'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {recentActivity.map((activity, index) => (
                        <div key={index} className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            <activity.icon className="w-5 h-5 text-primary" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium text-sm">{activity.title}</p>
                            <p className="text-xs text-text-secondary mt-1">{activity.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Notifications */}
                <Card className="shadow-lg">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Bell className="w-5 h-5 text-primary" />
                      {i18n.language === 'ar' ? 'الإشعارات الأخيرة' : 'Recent Notifications'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {notifications.length === 0 ? (
                      <div className="text-center py-8">
                        <Bell className="w-12 h-12 text-text-secondary mx-auto mb-3" />
                        <p className="text-text-secondary text-sm">
                          {i18n.language === 'ar' ? 'لا توجد إشعارات' : 'No notifications'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {notifications.map((notification, index) => (
                          <div
                            key={notification.id}
                            className={`p-3 rounded-lg border ${
                              !notification.is_read ? 'bg-primary/5 border-primary/20' : 'bg-gray-50 border-gray-200'
                            }`}
                          >
                            <p className="font-medium text-sm">{notification.title}</p>
                            <p className="text-xs text-text-secondary mt-1 line-clamp-2">
                              {notification.message}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Analytics Tab */}
            <TabsContent value="analytics" className="space-y-6">
              <Card className="shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    {i18n.language === 'ar' ? 'الإحصائيات التفصيلية' : 'Detailed Analytics'}
                  </CardTitle>
                  <CardDescription>
                    {i18n.language === 'ar'
                      ? 'تحليل شامل لأداء القضايا والخدمات'
                      : 'Comprehensive analysis of cases and services'
                    }
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-20">
                    <BarChart3 className="w-20 h-20 text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary text-lg">
                      {i18n.language === 'ar'
                        ? 'الإحصائيات التفصيلية قيد التطوير'
                        : 'Detailed analytics coming soon'
                      }
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default EnhancedDashboardPage;
